import { supabase } from '../lib/supabase';

/**
 * Faz upload de uma imagem para o bucket escola-logos
 * @param {File} file - Arquivo de imagem
 * @param {string} escolaId - ID da escola
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export const uploadEscolaLogo = async (file, escolaId) => {
  try {
    // Validar arquivo
    if (!file) {
      return { success: false, error: 'Nenhum arquivo selecionado' };
    }

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { success: false, error: 'Tipo de arquivo não permitido. Use JPEG, PNG, GIF ou WebP.' };
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      return { success: false, error: 'Arquivo muito grande. Máximo 5MB.' };
    }

    // Gerar nome único para o arquivo
    const fileExt = file.name.split('.').pop();
    const fileName = `logos/${escolaId}-${Date.now()}.${fileExt}`;

    // Fazer upload
    const { error } = await supabase.storage
      .from('escola-logos')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Erro no upload:', error);
      return { success: false, error: error.message };
    }

    // Obter URL pública
    const { data: urlData } = supabase.storage
      .from('escola-logos')
      .getPublicUrl(fileName);

    return { 
      success: true, 
      url: urlData.publicUrl,
      path: fileName
    };

  } catch (error) {
    console.error('Erro no upload:', error);
    return { success: false, error: 'Erro interno no upload' };
  }
};

/**
 * Remove uma imagem do bucket
 * @param {string} filePath - Caminho do arquivo no bucket
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteEscolaLogo = async (filePath) => {
  try {
    if (!filePath) {
      return { success: true }; // Não há arquivo para deletar
    }

    const { error } = await supabase.storage
      .from('escola-logos')
      .remove([filePath]);

    if (error) {
      console.error('Erro ao deletar arquivo:', error);
      return { success: false, error: error.message };
    }

    return { success: true };

  } catch (error) {
    console.error('Erro ao deletar arquivo:', error);
    return { success: false, error: 'Erro interno ao deletar arquivo' };
  }
};

/**
 * Atualiza o logotipo de uma escola (remove o antigo e faz upload do novo)
 * @param {File} file - Novo arquivo de imagem
 * @param {string} escolaId - ID da escola
 * @param {string} oldFilePath - Caminho do arquivo antigo (opcional)
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export const updateEscolaLogo = async (file, escolaId, oldFilePath) => {
  try {
    // Fazer upload do novo arquivo
    const uploadResult = await uploadEscolaLogo(file, escolaId);
    
    if (!uploadResult.success) {
      return uploadResult;
    }

    // Se há um arquivo antigo, removê-lo
    if (oldFilePath) {
      await deleteEscolaLogo(oldFilePath);
    }

    return uploadResult;

  } catch (error) {
    console.error('Erro ao atualizar logotipo:', error);
    return { success: false, error: 'Erro interno ao atualizar logotipo' };
  }
};
